import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Popover,
  Button,
  Checkbox,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActivityRow,
  ActivityDurationUnit,
  createActivity,
  listActivities,
  updateActivity,
  updateActivityStatus,
} from '../api/activities';
import { listCandidatures } from '../api/candidatures';
import { listUsers } from '../api/users';
import { listPostsSummary } from '../api/posts';
import { useAuth } from '../auth/AuthContext';
import { downloadRecordsCsv } from '../utils/csvExport';
import { deleteDocsInCollection } from '../api/batchDelete';
import { activityStatusLabel } from '../utils/statusLabels';
import { normalizeText } from '../utils/text';
import {
  EntityAutocomplete,
  type EntityOption,
} from '../components/EntityAutocomplete';

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString() : '—';

const DURATION_UNIT_OPTIONS: Array<{ value: ActivityDurationUnit; label: string }> = [
  { value: 'hours', label: 'Heures' },
  { value: 'days', label: 'Jours' },
  { value: 'months', label: 'Mois' },
];

function activityDurationLabel(a: ActivityRow): string {
  const value = a.durationValue ?? a.hours;
  const unit = a.durationUnit ?? 'hours';
  if (value == null) return '—';
  const suffix = unit === 'hours' ? 'h' : unit === 'days' ? 'j' : 'mois';
  return `${value} ${suffix}`;
}

const statusColor = (status?: string) => {
  switch (status) {
    case 'validated':
      return 'success';
    case 'rejected':
      return 'error';
    case 'pending':
    default:
      return 'warning';
  }
};

function dateToInputValue(d?: Date): string {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function inputValueToDate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
}

export const ActivitiesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const canEdit = role === 'admin' || role === 'cap' || role === 'ae';
  const canBulkDeleteActivities = role === 'admin';
  const { data, isLoading } = useQuery<ActivityRow[]>({
    queryKey: ['activities'],
    queryFn: () => listActivities(),
  });

  const { data: candidatures } = useQuery({
    queryKey: ['candidatures-for-activities'],
    queryFn: listCandidatures,
  });
  const { data: users } = useQuery({
    queryKey: ['users-for-activities'],
    queryFn: listUsers,
  });
  const { data: posts } = useQuery({
    queryKey: ['posts-summary-for-activities'],
    queryFn: listPostsSummary,
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [orderBy, setOrderBy] = React.useState<
    'candidature' | 'status' | 'date' | 'duration' | 'createdAt' | 'updatedAt'
  >('date');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');

  const [confirmAnchorEl, setConfirmAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [confirmDescription, setConfirmDescription] = React.useState('');
  const [pendingActivity, setPendingActivity] = React.useState<{
    ids: string[];
    status: 'validated' | 'rejected';
  } | null>(null);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [deleteError, setDeleteError] = React.useState<string | null>(null);
  const [deleting, setDeleting] = React.useState(false);

  const [editActivity, setEditActivity] = React.useState<ActivityRow | null>(null);
  const [editCandidatureId, setEditCandidatureId] = React.useState('');
  const [editActStatus, setEditActStatus] = React.useState<'validated' | 'rejected' | 'pending'>(
    'pending'
  );
  const [editActDate, setEditActDate] = React.useState('');
  const [editDurationValue, setEditDurationValue] = React.useState('');
  const [editDurationUnit, setEditDurationUnit] = React.useState<ActivityDurationUnit>('hours');
  const [editDescription, setEditDescription] = React.useState('');
  const [editSaving, setEditSaving] = React.useState(false);
  const [editError, setEditError] = React.useState<string | null>(null);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [createCandidatureId, setCreateCandidatureId] = React.useState('');
  const [createActStatus, setCreateActStatus] = React.useState<
    'validated' | 'rejected' | 'pending'
  >('pending');
  const [createActDate, setCreateActDate] = React.useState('');
  const [createDurationValue, setCreateDurationValue] = React.useState('');
  const [createDurationUnit, setCreateDurationUnit] = React.useState<ActivityDurationUnit>('hours');
  const [createDescription, setCreateDescription] = React.useState('');
  const [createSaving, setCreateSaving] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  const confirmOpen = Boolean(confirmAnchorEl);

  const handleRequestSort = (
    property:
      | 'candidature'
      | 'status'
      | 'date'
      | 'duration'
      | 'createdAt'
      | 'updatedAt'
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const userNameById = React.useMemo(() => {
    const map: Record<string, string> = {};
    users?.forEach((u) => {
      const fullName = `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim();
      map[u.id] = fullName || u.email || u.id;
    });
    return map;
  }, [users]);

  const postTitleById = React.useMemo(() => {
    const map: Record<string, string> = {};
    posts?.forEach((p) => {
      map[p.id] = p.title || p.id;
    });
    return map;
  }, [posts]);

  const candidatureLabelById = React.useMemo(() => {
    const map: Record<string, string> = {};
    candidatures?.forEach((c) => {
      const name = c.userId ? userNameById[c.userId] ?? c.userId : '';
      const postTitle = c.postId ? postTitleById[c.postId] ?? c.postId : '';
      const labelParts = [name, postTitle].filter(Boolean);
      if (labelParts.length) {
        map[c.id] = labelParts.join(' · ');
      }
    });
    return map;
  }, [candidatures, userNameById, postTitleById]);

  const candidatureEditOptions = React.useMemo<EntityOption[]>(() => {
    if (!candidatures?.length) return [];
    return candidatures.map((c) => ({
      id: c.id,
      label: candidatureLabelById[c.id] || 'Candidature',
    }));
  }, [candidatures, candidatureLabelById]);

  const filtered = React.useMemo(() => {
    if (!data) return [];
    return data.filter((a) => {
      const candidatureLabel = a.candidatureId
        ? candidatureLabelById[a.candidatureId] ?? a.candidatureId
        : '';
      const haystack = normalizeText(`${candidatureLabel} ${a.description ?? ''}`);
      const matchesSearch = haystack.includes(normalizeText(search));
      const matchesStatus =
        statusFilter === 'all' || (a.status ?? 'pending') === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, search, statusFilter, candidatureLabelById]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const factor = order === 'asc' ? 1 : -1;
      let av: string | Date | number | undefined;
      let bv: string | Date | number | undefined;

      const getCandidatureLabel = (x: ActivityRow) =>
        x.candidatureId
          ? candidatureLabelById[x.candidatureId] ?? x.candidatureId
          : '';

      switch (orderBy) {
        case 'candidature':
          av = getCandidatureLabel(a).toLowerCase();
          bv = getCandidatureLabel(b).toLowerCase();
          break;
        case 'status':
          av = (a.status ?? '').toLowerCase();
          bv = (b.status ?? '').toLowerCase();
          break;
        case 'duration':
          av = a.durationValue ?? a.hours ?? 0;
          bv = b.durationValue ?? b.hours ?? 0;
          break;
        case 'date':
          av = a.date ?? new Date(0);
          bv = b.date ?? new Date(0);
          break;
        case 'createdAt':
          av = a.createdAt ?? new Date(0);
          bv = b.createdAt ?? new Date(0);
          break;
        case 'updatedAt':
          av = a.updatedAt ?? new Date(0);
          bv = b.updatedAt ?? new Date(0);
          break;
        default:
          av = 0 as any;
          bv = 0 as any;
      }

      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * factor;
      }

      const as = String(av);
      const bs = String(bv);
      if (as < bs) return -1 * factor;
      if (as > bs) return 1 * factor;
      return 0;
    });
    return arr;
  }, [filtered, order, orderBy, candidatureLabelById]);

  const paginated = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleChangeStatusRequest = (
    event: React.MouseEvent<HTMLElement>,
    activityOrIds: ActivityRow | string[],
    status: 'validated' | 'rejected'
  ) => {
    const actionLabel = status === 'validated' ? 'valider' : 'rejeter';

    if (Array.isArray(activityOrIds)) {
      const ids = activityOrIds;
      if (!ids.length) return;
      setConfirmDescription(
        `Voulez-vous vraiment ${actionLabel} les ${ids.length} activités sélectionnées ?`
      );
      setPendingActivity({ ids, status });
    } else {
      const activity = activityOrIds;
      const candidatureLabel = activity.candidatureId
        ? candidatureLabelById[activity.candidatureId] ?? activity.candidatureId
        : 'cette activité';
      setConfirmDescription(
        `Voulez-vous vraiment ${actionLabel} l'activité liée à ${candidatureLabel} ?`
      );
      setPendingActivity({ ids: [activity.id], status });
    }

    setConfirmAnchorEl(event.currentTarget);
  };

  const handleConfirmActivity = async () => {
    if (!pendingActivity) return;
    await Promise.all(
      pendingActivity.ids.map((id) =>
        updateActivityStatus(id, pendingActivity.status)
      )
    );
    await queryClient.invalidateQueries({ queryKey: ['activities'] });
    setConfirmAnchorEl(null);
    setPendingActivity(null);
    setSelectedIds([]);
  };

  const handleCancelActivity = () => {
    setConfirmAnchorEl(null);
    setPendingActivity(null);
  };

  const allSelected =
    filtered.length > 0 &&
    filtered.every((a) => selectedIds.includes(a.id));
  const someSelected =
    filtered.length > 0 &&
    filtered.some((a) => selectedIds.includes(a.id)) &&
    !allSelected;

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(filtered.map((a) => a.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const openEditActivity = (a: ActivityRow) => {
    setEditActivity(a);
    setEditCandidatureId(a.candidatureId ?? '');
    setEditActStatus((a.status as 'validated' | 'rejected' | 'pending') ?? 'pending');
    setEditActDate(dateToInputValue(a.date));
    setEditDurationValue(
      a.durationValue != null ? String(a.durationValue) : a.hours != null ? String(a.hours) : ''
    );
    setEditDurationUnit(a.durationUnit ?? 'hours');
    setEditDescription(a.description ?? '');
    setEditError(null);
  };

  const closeEditActivity = () => {
    if (editSaving) return;
    setEditActivity(null);
    setEditError(null);
  };

  const openCreateActivity = () => {
    setEditActivity(null);
    setEditError(null);
    setCreateCandidatureId('');
    setCreateActStatus('pending');
    setCreateActDate(dateToInputValue(new Date()));
    setCreateDurationValue('');
    setCreateDurationUnit('hours');
    setCreateDescription('');
    setCreateError(null);
    setCreateOpen(true);
  };

  const closeCreateActivity = () => {
    if (createSaving) return;
    setCreateOpen(false);
    setCreateError(null);
  };

  const handleSaveCreateActivity = async () => {
    if (!createCandidatureId.trim()) {
      setCreateError('La candidature est obligatoire.');
      return;
    }
    const d = inputValueToDate(createActDate);
    if (!d) {
      setCreateError('Date invalide (AAAA-MM-JJ).');
      return;
    }
    const durationNum = Number(String(createDurationValue).replace(',', '.'));
    if (!Number.isFinite(durationNum)) {
      setCreateError('Duree invalide.');
      return;
    }
    setCreateSaving(true);
    setCreateError(null);
    try {
      await createActivity({
        candidatureId: createCandidatureId.trim(),
        status: createActStatus,
        date: d,
        durationValue: durationNum,
        durationUnit: createDurationUnit,
        description: createDescription,
      });
      await queryClient.invalidateQueries({ queryKey: ['activities'] });
      setCreateOpen(false);
      setPage(0);
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Création impossible.');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleSaveActivity = async () => {
    if (!editActivity) return;
    if (!editCandidatureId.trim()) {
      setEditError('Le candidatureId est obligatoire.');
      return;
    }
    const d = inputValueToDate(editActDate);
    if (!d) {
      setEditError('Date invalide (AAAA-MM-JJ).');
      return;
    }
    const durationNum = Number(String(editDurationValue).replace(',', '.'));
    if (!Number.isFinite(durationNum)) {
      setEditError('Duree invalide.');
      return;
    }
    setEditSaving(true);
    setEditError(null);
    try {
      await updateActivity(editActivity.id, {
        candidatureId: editCandidatureId.trim(),
        status: editActStatus,
        date: d,
        durationValue: durationNum,
        durationUnit: editDurationUnit,
        description: editDescription,
      });
      await queryClient.invalidateQueries({ queryKey: ['activities'] });
      setEditActivity(null);
    } catch (e) {
      setEditError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleConfirmDeleteSelected = async () => {
    if (!selectedIds.length) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteDocsInCollection('activities', selectedIds);
      await queryClient.invalidateQueries({ queryKey: ['activities'] });
      setSelectedIds([]);
      setDeleteDialogOpen(false);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : "La suppression a échoué.");
    } finally {
      setDeleting(false);
    }
  };

  const handleExportCsv = () => {
    const rows: Array<Record<string, unknown>> = sorted.map((a) => {
      const cid = a.candidatureId ?? '';
      return {
        activityId: a.id,
        candidatureId: cid,
        candidatureLabel: cid ? candidatureLabelById[cid] ?? '' : '',
        status: a.status ?? '',
        date: a.date,
        durationValue: a.durationValue ?? a.hours ?? '',
        durationUnit: a.durationUnit ?? 'hours',
        durationLabel: activityDurationLabel(a),
        description: a.description ?? '',
        createdAt: a.createdAt,
        updatedAt: a.updatedAt,
      };
    });
    downloadRecordsCsv('activites', rows);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Activités
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          gap: 1,
          mb: 1,
        }}
      >
        {canEdit && (
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openCreateActivity}>
            Ajouter
          </Button>
        )}
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownloadIcon />}
          disabled={!sorted.length}
          onClick={handleExportCsv}
        >
          Exporter CSV
        </Button>
      </Box>
      {canEdit && (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          size="small"
          label="Rechercher"
          placeholder="Candidat, poste, description..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Statut</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Statut"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="pending">En attente</MenuItem>
            <MenuItem value="validated">Validée</MenuItem>
            <MenuItem value="rejected">Rejetée</MenuItem>
          </Select>
        </FormControl>
      </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          gap: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {selectedIds.length
            ? `${selectedIds.length} sélectionnée(s)`
            : 'Aucune sélection'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={!selectedIds.length}
            onClick={(event) =>
              handleChangeStatusRequest(event, selectedIds, 'validated')
            }
          >
            Valider la sélection
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={!selectedIds.length}
            onClick={(event) =>
              handleChangeStatusRequest(event, selectedIds, 'rejected')
            }
          >
            Rejeter la sélection
          </Button>
          {canBulkDeleteActivities && (
            <Button
              size="small"
              variant="outlined"
              color="error"
              disabled={!selectedIds.length}
              onClick={() => {
                setDeleteError(null);
                setDeleteDialogOpen(true);
              }}
            >
              Supprimer la sélection
            </Button>
          )}
        </Box>
      </Box>
      {deleteError && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setDeleteError(null)}>
          {deleteError}
        </Alert>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {canEdit && (
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    indeterminate={someSelected}
                    checked={allSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </TableCell>
              )}
              <TableCell
                sortDirection={orderBy === 'candidature' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'candidature'}
                  direction={orderBy === 'candidature' ? order : 'asc'}
                  onClick={() => handleRequestSort('candidature')}
                >
                  Candidat · Poste
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'duration' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'duration'}
                  direction={orderBy === 'duration' ? order : 'asc'}
                  onClick={() => handleRequestSort('duration')}
                >
                  Duree
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'date' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'date'}
                  direction={orderBy === 'date' ? order : 'asc'}
                  onClick={() => handleRequestSort('date')}
                >
                  Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'createdAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Créée le
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'updatedAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  Mise à jour le
                </TableSortLabel>
              </TableCell>
              <TableCell>Description</TableCell>
              {canEdit && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={canEdit ? 9 : 8}>Chargement...</TableCell>
              </TableRow>
            )}
            {!isLoading &&
              paginated.map((a) => (
                <TableRow key={a.id} hover selected={canEdit && selectedIds.includes(a.id)}>
                  {canEdit && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIds.includes(a.id)}
                        onChange={() => toggleOne(a.id)}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {a.candidatureId
                      ? candidatureLabelById[a.candidatureId] ??
                        a.candidatureId
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={activityStatusLabel(a.status)}
                      color={statusColor(a.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{activityDurationLabel(a)}</TableCell>
                  <TableCell>{formatDate(a.date)}</TableCell>
                  <TableCell>{formatDate(a.createdAt)}</TableCell>
                  <TableCell>{formatDate(a.updatedAt)}</TableCell>
                  <TableCell>{a.description ?? '—'}</TableCell>
                  {canEdit && (
                    <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          flexWrap: 'nowrap',
                          gap: 0.25,
                        }}
                      >
                        <Tooltip title="Modifier l'activité">
                          <IconButton size="small" onClick={() => openEditActivity(a)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {a.status !== 'validated' && (
                          <Tooltip title="Valider l'activité">
                            <IconButton
                              size="small"
                              onClick={(event) =>
                                handleChangeStatusRequest(event, a, 'validated')
                              }
                            >
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {a.status !== 'rejected' && (
                          <Tooltip title="Rejeter l'activité">
                            <IconButton
                              size="small"
                              onClick={(event) =>
                                handleChangeStatusRequest(event, a, 'rejected')
                              }
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            {!isLoading && !filtered.length && (
              <TableRow>
                <TableCell colSpan={canEdit ? 9 : 8}>
                  <Typography variant="body2" color="text.secondary">
                    Aucune activité trouvée.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50, 100, 500]}
      />
      <Dialog open={createOpen} onClose={closeCreateActivity} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvelle activité</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {createError && (
            <Alert severity="error" onClose={() => setCreateError(null)}>
              {createError}
            </Alert>
          )}
          <EntityAutocomplete
            label="Candidature"
            required
            options={candidatureEditOptions}
            valueId={createCandidatureId}
            onChangeId={setCreateCandidatureId}
            disabled={createSaving}
            loading={!candidatures}
            helperText="Rechercher par candidat et poste"
            orphanLabel={createCandidatureId.trim() ? 'Candidature (hors liste chargée)' : undefined}
          />
          <TextField
            label="Date"
            type="date"
            value={createActDate}
            onChange={(e) => setCreateActDate(e.target.value)}
            disabled={createSaving}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Valeur de duree"
            value={createDurationValue}
            onChange={(e) => setCreateDurationValue(e.target.value)}
            disabled={createSaving}
          />
          <FormControl size="small">
            <InputLabel id="create-act-duration-unit-label">Unite</InputLabel>
            <Select
              labelId="create-act-duration-unit-label"
              label="Unite"
              value={createDurationUnit}
              onChange={(e) => setCreateDurationUnit(e.target.value as ActivityDurationUnit)}
              disabled={createSaving}
            >
              {DURATION_UNIT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Description"
            value={createDescription}
            onChange={(e) => setCreateDescription(e.target.value)}
            disabled={createSaving}
            multiline
            minRows={2}
          />
          <FormControl size="small">
            <InputLabel id="create-act-status-label">Statut</InputLabel>
            <Select
              labelId="create-act-status-label"
              label="Statut"
              value={createActStatus}
              onChange={(e) =>
                setCreateActStatus(e.target.value as 'validated' | 'rejected' | 'pending')
              }
              disabled={createSaving}
            >
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="validated">Validée</MenuItem>
              <MenuItem value="rejected">Rejetée</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCreateActivity} color="inherit" disabled={createSaving}>
            Annuler
          </Button>
          <Button
            onClick={() => void handleSaveCreateActivity()}
            variant="contained"
            disabled={createSaving}
          >
            {createSaving ? 'Création…' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editActivity)} onClose={closeEditActivity} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier l&apos;activité</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {editError && (
            <Alert severity="error" onClose={() => setEditError(null)}>
              {editError}
            </Alert>
          )}
          <EntityAutocomplete
            label="Candidature"
            required
            options={candidatureEditOptions}
            valueId={editCandidatureId}
            onChangeId={setEditCandidatureId}
            disabled={editSaving}
            loading={!candidatures}
            helperText="Rechercher par candidat ou poste"
            orphanLabel={editCandidatureId.trim() ? 'Candidature (hors liste chargée)' : undefined}
          />
          <TextField
            label="Date"
            type="date"
            value={editActDate}
            onChange={(e) => setEditActDate(e.target.value)}
            disabled={editSaving}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Valeur de duree"
            type="text"
            value={editDurationValue}
            onChange={(e) => setEditDurationValue(e.target.value)}
            disabled={editSaving}
          />
          <FormControl size="small">
            <InputLabel id="edit-act-duration-unit-label">Unite</InputLabel>
            <Select
              labelId="edit-act-duration-unit-label"
              label="Unite"
              value={editDurationUnit}
              onChange={(e) => setEditDurationUnit(e.target.value as ActivityDurationUnit)}
              disabled={editSaving}
            >
              {DURATION_UNIT_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Description"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            disabled={editSaving}
            multiline
            minRows={2}
          />
          <FormControl size="small">
            <InputLabel id="edit-act-status-label">Statut</InputLabel>
            <Select
              labelId="edit-act-status-label"
              label="Statut"
              value={editActStatus}
              onChange={(e) =>
                setEditActStatus(e.target.value as 'validated' | 'rejected' | 'pending')
              }
              disabled={editSaving}
            >
              <MenuItem value="pending">En attente</MenuItem>
              <MenuItem value="validated">Validée</MenuItem>
              <MenuItem value="rejected">Rejetée</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditActivity} color="inherit" disabled={editSaving}>
            Annuler
          </Button>
          <Button onClick={() => void handleSaveActivity()} variant="contained" disabled={editSaving}>
            {editSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Popover
        open={canEdit && confirmOpen}
        anchorEl={confirmAnchorEl}
        onClose={handleCancelActivity}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, maxWidth: 320 }}>
          <Typography variant="subtitle2" gutterBottom>
            Confirmer l&apos;action
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {confirmDescription}
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button onClick={handleCancelActivity} color="inherit" size="small">
              Annuler
            </Button>
            <Button
              onClick={handleConfirmActivity}
              color="primary"
              variant="contained"
              size="small"
            >
              Confirmer
            </Button>
          </Box>
        </Box>
      </Popover>
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Supprimer les activités sélectionnées</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedIds.length} activité(s) seront définitivement supprimées (admin uniquement,
            règles Firestore).
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Annuler
          </Button>
          <Button onClick={handleConfirmDeleteSelected} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Suppression…' : 'Supprimer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

